import React from 'react';
import PropTypes from 'prop-types';

const DataLoader = ({
  loading = false,
  error = null,
  skeleton = null,
  children,
  className = '',
  noData = false,
  noDataMessage = null,
  ...props
}) => {
  // Show error state if error exists
  if (error) {
    return (
      <div className={`data-loader data-loader--error ${className}`} {...props}>
        {typeof error === 'string' ? (
          <div className='data-loader-error-message'>
            <span>{error}</span>
          </div>
        ) : (
          error
        )}
      </div>
    );
  }

  // Show skeleton if loading
  if (loading) {
    // If custom skeleton provided, use it
    if (skeleton) {
      return (
        <div
          className={`data-loader data-loader--loading ${className}`}
          {...props}
        >
          {skeleton}
        </div>
      );
    }
    // Return null when loading but no skeleton provided
    // This prevents rendering children during loading state
    return null;
  }

  if (!loading && noData) {
    // Empty state when no data available
    return (
      <div className={`data-loader data-loader--empty ${className}`} {...props}>
        {noDataMessage || (
          <div className='data-loader-empty-message'>No content</div>
        )}
      </div>
    );
  }

  // Render children when not loading
  if (children) {
    return (
      <div
        className={`data-loader data-loader--loaded ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }

  // Fallback for edge cases
  return null;
};

DataLoader.propTypes = {
  /** Whether data is currently loading */
  loading: PropTypes.bool,

  /** Error state (string message or React node) */
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** Custom skeleton component to render while loading */
  skeleton: PropTypes.node,

  /** Content to render when not loading */
  children: PropTypes.node,

  /** Whether there is no data to show (renders the empty state when not loading) */
  noData: PropTypes.bool,

  /** Additional CSS classes */
  className: PropTypes.string,

  /** Message to display when no data is available */
  noDataMessage: PropTypes.node,
};

export default DataLoader;
